/**
 * Zoom keyframes: the click-driven timeline data the camera interpolates to.
 */
import { clamp } from './format'
import { createId } from './id'

export interface ZoomKeyframe {
  id: string
  /** Seconds into the recording. */
  t: number
  /** Zoom center, normalized to the recorded frame (0..1). */
  x: number
  y: number
  /** Target zoom factor, 1 = no zoom. */
  zoom: number
  /** How long the camera holds this zoom before easing back out, in seconds. */
  duration: number
}

export const DEFAULT_ZOOM = 2
export const DEFAULT_KEYFRAME_DURATION = 2.5
export const ZOOM_RANGE = { min: 1, max: 4, step: 0.1 } as const
export const KEYFRAME_DURATION_RANGE = { min: 0.5, max: 10, step: 0.1 } as const
/** Keyframes closer together than this are treated as the same intent. */
export const KEYFRAME_MIN_GAP = 0.25

export interface KeyframeSeed {
  t?: number
  x?: number
  y?: number
  zoom?: number
  duration?: number
}

/** Build a valid keyframe from partial user input. */
export function makeKeyframe(seed: KeyframeSeed = {}): ZoomKeyframe {
  return {
    id: createId('kf'),
    t: Math.max(0, seed.t ?? 0),
    x: clamp(seed.x ?? 0.5, 0, 1),
    y: clamp(seed.y ?? 0.5, 0, 1),
    zoom: clampZoom(seed.zoom ?? DEFAULT_ZOOM),
    duration: clampDuration(seed.duration ?? DEFAULT_KEYFRAME_DURATION),
  }
}

export function clampZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return DEFAULT_ZOOM
  return clamp(zoom, ZOOM_RANGE.min, ZOOM_RANGE.max)
}

export function clampDuration(duration: number): number {
  if (!Number.isFinite(duration)) return DEFAULT_KEYFRAME_DURATION
  return clamp(duration, KEYFRAME_DURATION_RANGE.min, KEYFRAME_DURATION_RANGE.max)
}

/** Sort by timestamp, falling back to id so ordering is always stable. */
export function sortKeyframes(keyframes: readonly ZoomKeyframe[]): ZoomKeyframe[] {
  return [...keyframes].sort((a, b) => a.t - b.t || a.id.localeCompare(b.id))
}

/** Clamp a keyframe's time into `0..maxTime` (and everything else into range). */
export function clampKeyframe(keyframe: ZoomKeyframe, maxTime: number): ZoomKeyframe {
  const limit = Number.isFinite(maxTime) && maxTime > 0 ? maxTime : keyframe.t
  return {
    ...keyframe,
    t: clamp(keyframe.t, 0, Math.max(0, limit)),
    x: clamp(keyframe.x, 0, 1),
    y: clamp(keyframe.y, 0, 1),
    zoom: clampZoom(keyframe.zoom),
    duration: clampDuration(keyframe.duration),
  }
}

/** Apply a partial patch to a keyframe, keeping every value valid. */
export function patchKeyframe(keyframe: ZoomKeyframe, patch: Partial<ZoomKeyframe>, maxTime: number): ZoomKeyframe {
  return clampKeyframe({ ...keyframe, ...patch }, maxTime)
}

/**
 * Keyframe whose zoom window is active at time `t`. The window is
 * `k.t <= t <= k.t + k.duration`. When several overlap the most recently
 * started one wins, which keeps rapid clicks readable.
 */
export function activeKeyframeAt(keyframes: readonly ZoomKeyframe[], t: number): ZoomKeyframe | null {
  if (!Number.isFinite(t) || keyframes.length === 0) return null
  let winner: ZoomKeyframe | null = null
  for (const keyframe of keyframes) {
    if (t < keyframe.t - 1e-9) continue
    if (t > keyframe.t + keyframe.duration + 1e-9) continue
    if (!winner || keyframe.t >= winner.t) winner = keyframe
  }
  return winner
}

/**
 * Drop keyframes that start within {@link KEYFRAME_MIN_GAP} of the previous
 * kept one — protects against double-fires from pointer events.
 */
export function dedupeKeyframes(keyframes: readonly ZoomKeyframe[], minGap = KEYFRAME_MIN_GAP): ZoomKeyframe[] {
  const sorted = sortKeyframes(keyframes)
  const kept: ZoomKeyframe[] = []
  for (const keyframe of sorted) {
    const previous = kept[kept.length - 1]
    if (previous && keyframe.t - previous.t < minGap) continue
    kept.push(keyframe)
  }
  return kept
}

/** Human readable label for a keyframe, e.g. `1.4s · 2.0x`. */
export function keyframeLabel(keyframe: ZoomKeyframe): string {
  return `${keyframe.t.toFixed(1)}s · ${keyframe.zoom.toFixed(1)}x`
}
